/**
 * Lead capture: validation, the conversation summary and the POST /chat/lead
 * call. Pure apart from `submitLead`, so scripts/test-config.mjs can pin the
 * rules against the backend's (backend/functions/chat_lead/handler.py).
 */
import { CONTACT_LIMITS, EMAIL_RE, validateContact, type ContactField } from './details';
import type { StoredMessage } from './types';

/** Mirrors the backend's `_is_valid_email`: the details form's one rule (details.ts). */
export const LEAD_EMAIL_RE = EMAIL_RE;

/**
 * Mirrors the backend's "Field too long" limits. Name, email and phone are
 * the details form's own (details.ts) — one limit for each across the
 * product, whatever the form is for.
 */
export const LEAD_LIMITS = { ...CONTACT_LIMITS, message: 5000 } as const;

// ── What the form asks (docs-company ADR-114 Amendment 2) ─────────────
// The business sets it under Setup › Integrations › Leads; GET /chat/config
// hands it over as `leadForm`, and backend common/lead_form.py holds the
// answers to the same shape. Name and email are always required.

/** How a built-in field (phone, message) is drawn. */
export type LeadFieldMode = 'hidden' | 'optional' | 'required';
export type LeadExtraFieldType = 'text' | 'textarea' | 'select';

export interface LeadExtraField {
  /** `f_` + 6–24 of [a-z0-9], minted by the console and never reused. */
  id: string;
  /** The business's own words, shown as typed in every language. */
  label: string;
  type: LeadExtraFieldType;
  required: boolean;
  /** A select's choices; empty for the other types. */
  options: string[];
}

export interface LeadFormConfig {
  phone: LeadFieldMode;
  message: LeadFieldMode;
  extraFields: LeadExtraField[];
}

/** Today's form: what every widget drew before the setting existed. */
export const DEFAULT_LEAD_FORM: LeadFormConfig = { phone: 'optional', message: 'optional', extraFields: [] };

/** Mirrors common/lead_form.py VALUE_MAX: how long an answer may be, per type. */
export const LEAD_FIELD_LIMITS: Record<LeadExtraFieldType, number> = { text: 200, textarea: 2000, select: 60 };
const MAX_EXTRA_FIELDS = 5;
const FIELD_ID_RE = /^f_[a-z0-9]{6,24}$/;
const MODES: readonly string[] = ['hidden', 'optional', 'required'];
const TYPES: readonly string[] = ['text', 'textarea', 'select'];

/**
 * The server's `leadForm`, read defensively. The server has already
 * normalised it; this only makes sure a malformed or absent value draws
 * today's form, and a malformed question is left out rather than drawn wrong.
 */
export function parseLeadForm(raw: unknown): LeadFormConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return DEFAULT_LEAD_FORM;
  const o = raw as Record<string, unknown>;
  const mode = (value: unknown): LeadFieldMode => (typeof value === 'string' && MODES.includes(value) ? (value as LeadFieldMode) : 'optional');
  const extraFields: LeadExtraField[] = [];
  const seen = new Set<string>();
  for (const item of Array.isArray(o.extraFields) ? o.extraFields : []) {
    if (extraFields.length >= MAX_EXTRA_FIELDS) break;
    if (!item || typeof item !== 'object') continue;
    const f = item as Record<string, unknown>;
    const id = typeof f.id === 'string' ? f.id : '';
    const label = typeof f.label === 'string' ? f.label.trim() : '';
    const type = typeof f.type === 'string' && TYPES.includes(f.type) ? (f.type as LeadExtraFieldType) : null;
    if (!FIELD_ID_RE.test(id) || seen.has(id) || !label || !type) continue;
    const options = Array.isArray(f.options) ? f.options.filter((s): s is string => typeof s === 'string' && s.trim() !== '').map((s) => s.trim()) : [];
    if (type === 'select' && options.length < 2) continue;
    seen.add(id);
    extraFields.push({ id, label, type, required: f.required === true, options: type === 'select' ? options : [] });
  }
  return { phone: mode(o.phone), message: mode(o.message), extraFields };
}

/** The backend truncates the summary at 10,000 characters; stay under it. */
const SUMMARY_MAX_CHARS = 9_000;

export interface LeadInput {
  name: string;
  email: string;
  /**
   * Optional. Checked for LENGTH only — deliberately no format rule, the way
   * the booking details form has always taken a number: "+44 (0)7700 900123",
   * "0039 06 1234567" and an extension are all real, and a regex tight enough
   * to be worth having rejects some of them. A client would rather ring a
   * number that needs a glance than not have one.
   */
  phone: string;
  message: string;
  /**
   * Answers to the business's own questions, by field id. A drawn question is
   * always sent — empty when unanswered — so the backend can tell "left
   * empty" (refused when required) from "never drawn" (a form older than
   * the setting, kept). Omitted by callers that draw no such questions.
   */
  fields?: Record<string, string>;
  /**
   * Honeypot. A real form always sends '' — the field is off-screen and hidden
   * from assistive tech, so only a bot filling every input reaches it.
   */
  website: string;
}

export type LeadValidation =
  | { ok: true; value: LeadInput }
  /** `field` is 'name' | 'email' | 'phone' | 'message', or an extra field's id. */
  | { ok: false; field: string; reason: 'required' | 'invalid' | 'tooLong' };

/**
 * The contact fields the lead form draws, and the ones it requires: name and
 * email always, the phone unless the business hid it (ADR-114 Am.2).
 */
export function leadContact(form: LeadFormConfig = DEFAULT_LEAD_FORM): { fields: ContactField[]; required: ContactField[] } {
  return {
    fields: form.phone === 'hidden' ? ['name', 'email'] : ['name', 'email', 'phone'],
    required: form.phone === 'required' ? ['name', 'email', 'phone'] : ['name', 'email'],
  };
}

/**
 * Checked in the order the form shows the fields; the first problem wins.
 * The contact fields by the details form's own rules (details.ts), then the
 * business's message and questions. A hidden built-in is sent as nothing at
 * all, whatever it holds.
 */
export function validateLead(raw: LeadInput, form: LeadFormConfig = DEFAULT_LEAD_FORM): LeadValidation {
  const { fields: contactFields, required } = leadContact(form);
  const contact = validateContact({ name: raw.name, email: raw.email, phone: raw.phone }, contactFields, required);
  if (!contact.ok) return contact;
  const { name = '', email = '', phone = '' } = contact.value;
  const message = form.message === 'hidden' ? '' : (raw.message ?? '').trim();
  if (form.message === 'required' && !message) return { ok: false, field: 'message', reason: 'required' };
  if (message.length > LEAD_LIMITS.message) return { ok: false, field: 'message', reason: 'tooLong' };

  const fields: Record<string, string> = {};
  for (const field of form.extraFields) {
    const typed = (raw.fields?.[field.id] ?? '').trim();
    // One line for anything but a long answer, as the backend stores it.
    const value = field.type === 'textarea' ? typed : typed.split(/\s+/).filter(Boolean).join(' ');
    if (field.required && !value) return { ok: false, field: field.id, reason: 'required' };
    if (value.length > LEAD_FIELD_LIMITS[field.type]) return { ok: false, field: field.id, reason: 'tooLong' };
    if (field.type === 'select' && value && !field.options.includes(value)) return { ok: false, field: field.id, reason: 'invalid' };
    fields[field.id] = value;
  }

  const value: LeadInput = { name, email, phone, message, website: raw.website ?? '' };
  if (form.extraFields.length) value.fields = fields;
  return { ok: true, value };
}

/**
 * The last `turns` exchanges as plain text for the enquiry email, so the
 * operator reads what the visitor asked before they left their details.
 * Failure bubbles are skipped; operator replies are kept (they are context).
 */
export function summariseConversation(messages: StoredMessage[], turns = 10): string {
  const lines = messages
    .filter((m) => !m.error && m.content.trim())
    .slice(-turns)
    .map((m) => {
      const who = m.role === 'user' ? 'Visitor' : m.role === 'admin' ? 'Operator' : 'Assistant';
      return `${who}: ${m.content.trim()}`;
    });
  const text = lines.join('\n');
  return text.length > SUMMARY_MAX_CHARS ? text.slice(text.length - SUMMARY_MAX_CHARS) : text;
}

export type LeadOutcome =
  | { kind: 'ok' }
  /** 403 lead_capture_disabled: the operator turned it off after this page loaded — hide the chip. */
  | { kind: 'disabled' }
  /** 403 origin_not_allowed / assistant_unavailable: same handling as a chat message. */
  | { kind: 'blocked'; code: 'origin_not_allowed' | 'assistant_unavailable' }
  /** 400: the backend rejected a field the client check let through. */
  | { kind: 'invalid' }
  /** 403 human_check_required (ADR-123): run the check, send once more. `humanCheck` is the refusal's config. */
  | { kind: 'human_check'; humanCheck: unknown }
  /** 429 lead_cap, 5xx, network, timeout: worth a retry. */
  | { kind: 'retry'; code: string };

export interface LeadRequest extends LeadInput {
  tenantId: string;
  sessionId?: string;
  conversation_summary: string;
  /** The human-check pass (ADR-123), when there is one. */
  humanPass?: string;
}

export async function submitLead(apiUrl: string, body: LeadRequest, timeoutMs = 20_000): Promise<LeadOutcome> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${apiUrl}/chat/lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (res.ok) return { kind: 'ok' };
    const detail = await res.json().catch(() => ({}));
    const code = detail && typeof detail.code === 'string' ? detail.code : '';
    if (res.status === 403) {
      if (code === 'lead_capture_disabled') return { kind: 'disabled' };
      if (code === 'human_check_required') return { kind: 'human_check', humanCheck: detail.humanCheck };
      if (code === 'origin_not_allowed' || code === 'assistant_unavailable') return { kind: 'blocked', code };
      return { kind: 'retry', code: code || 'forbidden' };
    }
    if (res.status === 400) return { kind: 'invalid' };
    return { kind: 'retry', code: code || `http_${res.status}` };
  } catch (err) {
    return { kind: 'retry', code: ctrl.signal.aborted ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}
